import React, { useRef } from 'react';
import set from 'lodash/set';

import CredentialsSection from './CredentialsSection';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import MultiselectChips from 'src/presentation/components/forms/MultiselectChips';
import NameValueListEditor from 'src/presentation/components/pages/integrations/editorComponents/NameValueListEditor';
import Switch from 'src/presentation/components/forms/Switch';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useOnSave from './hooks/useOnSave';
import webhooksResource from 'src/actions/integrations/webhooksResource';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { ChangeHandler, NameValueList } from 'src/presentation/components/pages/integrations/types';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import { WrappedField } from 'src/presentation/components/pages/integrations/editorComponents/EditorFields';
import { convertHeaderKeysToHeadersArray } from './hooks/useWebhooks';
import {
  EventType,
  getFilteredWebhookEventOptions,
  webhookDefaultConfiguration,
} from 'src/actions/integrations/webhook';
import useCustomChannelsConfiguration from 'src/presentation/components/pages/shared/hooks/useCustomChannelsConfiguration';
import AutomaticallyDisabledWebhookErrorBanner from './AutomaticallyDisabledWebhookErrorBanner';
import WebhookLogs from './WebhookLogs';

const WebhooksEditor = ({ errors, item, root }): JSX.Element => {
  const { isFeatureEnabled: isCustomChannelsEnabled } = useCustomChannelsConfiguration();
  const { add, update, validate } = webhooksResource.effects;
  const { resetErrors } = webhooksResource.actions;

  const workingItem = item || webhookDefaultConfiguration;
  const customHeadersRef = useRef<NameValueList>(convertHeaderKeysToHeadersArray(workingItem.headersKeys));
  const customHeadersMasked = workingItem.headersKeys;
  const isFeaturedEnabled = useIsFeatureEnabled();

  const excludedEvents: EventType[] = [];
  if (!isCustomChannelsEnabled) {
    excludedEvents.push(EventType.CustomChannelMessageSent);
  }

  if (!isFeaturedEnabled('customAttributes')) {
    excludedEvents.push(
      EventType.ConversationCustomAttributeAddedEventType,
      EventType.ConversationCustomAttributeRemovedEventType
    );
  }

  const webhookEventOptions = getFilteredWebhookEventOptions(...excludedEvents);

  const formContext = useForm({
    add,
    defaultItem: webhookDefaultConfiguration,
    errors,
    item,
    onSave: useOnSave({ customHeadersRef }),
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, onSave, workingStateRef, resetError } = formContext;

  const header = isNew ? 'Create Webhook' : `Edit ${item.name}`;

  const fieldChangeHandler =
    (attr: string): ChangeHandler =>
    value => {
      resetError({ attr, errors });
      set(workingStateRef.current, attr, value);
    };

  const headerListChangeHandler = (newList: NameValueList): void => {
    customHeadersRef.current = newList;
  };

  return (
    <Form context={formContext}>
      <H1 data-aid="form-header">{header}</H1>
      <AutomaticallyDisabledWebhookErrorBanner
        enabled={workingItem.enabled}
        webhookDisabledAt={workingItem.disabledAtDueToRetryLimit}
      />
      <Body data-aid="webhooks-editor">
        <Row>
          <WrappedField
            attr="name"
            autoFocus={true}
            data-aid="name"
            description="This is the name that will be displayed throughout Gladly"
            label="Name"
            onChange={fieldChangeHandler('name')}
            placeholder="i.e. Send Survey"
          />
          <Switch data-aid="enabled-toggle" attr="enabled" label="Enabled" />
        </Row>
        <Row>
          <WrappedField
            attr="url"
            data-aid="url"
            description="The URL where you'd like to send events"
            label="Webhook URL"
            onChange={fieldChangeHandler('url')}
            placeholder="e.g. https://customer.company.com"
          />
        </Row>
        <Row>
          <MultiselectChips attr="events" data-aid="events" label="Events" options={webhookEventOptions} />
        </Row>

        <CredentialsSection
          attr="credentials"
          hasCredentials={(item ?? {}).hasCredentials}
          onAttrChange={fieldChangeHandler}
        />

        <Section header="HTTP Headers" description={`Custom HTTP headers (e.g. X-API-Key) to include in every request`}>
          <Row>
            <NameValueListEditor
              addButtonLabel="Add a header"
              dataAid="custom-header-editor"
              items={customHeadersRef.current}
              maxItemCount={10}
              maskedItemNames={customHeadersMasked}
              nameColumnLabel="Header"
              valueColumnLabel="Value"
              onChange={headerListChangeHandler}
            />
          </Row>
        </Section>
        <Section header="Logs" description="Logs may take up to 30s to appear. Reload the page to see new logs.">
          <WebhookLogs webhookId={item?.id} />
        </Section>
      </Body>

      <Footer>
        <Button data-aid="editor-cancel" type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid="editor-save" onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

export default WebhooksEditor;
